import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { api } from "../api/axios";
import InstallUpgradeNotice from "../components/InstallUpgradeNotice";
import { EmptyState, IconTile, PageHeader, Pill } from "../components/ui";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import PowerOutlinedIcon from "@mui/icons-material/PowerOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const STATUS_META = {
  active: { tone: "success", label: "פעיל" },
  pending: { tone: "warning", label: "ממתין" },
  disabled: { tone: "neutral", label: "מושבת" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status || "active"] || { tone: "neutral", label: status };
  return (
    <Pill tone={meta.tone}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
      {meta.label}
    </Pill>
  );
}

function SiteKey({ value, copied, onCopy, full }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        pl: 1.25,
        pr: 0.25,
        py: 0.25,
        borderRadius: 1.25,
        bgcolor: (t) => t.aw.soft,
        border: 1,
        borderColor: "divider",
        maxWidth: full ? "100%" : 210,
        width: full ? "100%" : "auto",
      }}
    >
      <Box
        component="code"
        dir="ltr"
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          fontSize: "0.75rem",
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Box>
      <Tooltip title={copied ? "הועתק" : "העתק Site Key"}>
        <IconButton size="small" onClick={onCopy} aria-label="העתק Site Key" sx={{ color: copied ? "success.main" : "text.secondary" }}>
          {copied ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export default function Sites() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [enableWidget, setEnableWidget] = useState(true);
  const [enablePrivacy, setEnablePrivacy] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [wpVerifyDialog, setWpVerifyDialog] = useState({ open: false, loading: false, installed: false });
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  const load = async () => {
    const { data } = await api.get("/api/sites");
    setSites(data.sites || []);
    setLoaded(true);
  };

  useEffect(() => {
    load().catch((err) => {
      setLoaded(true);
      setError(err?.response?.data?.message || "שגיאה");
    });
  }, []);

  const create = async () => {
    setError("");
    try {
      const { data } = await api.post("/api/sites", {
        name,
        domain,
        status: enableWidget ? "active" : "disabled",
        privacyEnabled: enablePrivacy,
      });
      const newId = data?.site?.id;
      setName("");
      setDomain("");
      setDialogOpen(false);
      await load();
      if (newId) nav(`/sites/${newId}/onboarding`);
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה");
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("sites_confirm_delete", "למחוק את האתר?"))) return;
    await api.delete(`/api/sites/${id}`);
    await load();
  };

  const handleMenuOpen = (event, site) => {
    setAnchorEl(event.currentTarget);
    setSelectedSite(site);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSite(null);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1600);
  };

  const handleWpConnect = async (site) => {
    handleMenuClose();
    setSelectedSite(site);
    setWpVerifyDialog({ open: true, loading: true, installed: false });

    try {
      const { data } = await api.get(`/api/sites/${site.id}/verify-plugin`);
      if (data.installed) {
        // Plugin found, go directly
        const domains = (site.domain || "").split(",").map((d) => d.trim()).filter(Boolean);
        const primaryDomain = domains[0] || "";
        const wpUrl = `https://${primaryDomain}/wp-admin/admin.php?page=accss-settings&accss_auto_key=${site.site_key}`;
        window.open(wpUrl, "_blank");
        setWpVerifyDialog({ open: false, loading: false, installed: true });
      } else {
        // Plugin not found
        setWpVerifyDialog({ open: true, loading: false, installed: false });
      }
    } catch (err) {
      setWpVerifyDialog({ open: true, loading: false, installed: false });
    }
  };

  const proceedWpConnect = () => {
    const domains = (selectedSite?.domain || "").split(",").map((d) => d.trim()).filter(Boolean);
    const primaryDomain = domains[0] || "";
    const serverUrl = window.location.origin;
    const wpUrl = `https://${primaryDomain}/wp-admin/admin.php?page=accss-settings&accss_auto_key=${selectedSite?.site_key}&accss_server_url=${encodeURIComponent(serverUrl)}`;
    window.open(wpUrl, "_blank");
    setWpVerifyDialog({ ...wpVerifyDialog, open: false });
  };

  const counts = useMemo(() => {
    const c = { all: sites.length, active: 0, disabled: 0 };
    sites.forEach((s) => {
      if ((s.status || "active") === "active") c.active += 1;
      else c.disabled += 1;
    });
    return c;
  }, [sites]);

  const visibleSites = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sites.filter((s) => {
      const st = s.status || "active";
      if (statusFilter === "active" && st !== "active") return false;
      if (statusFilter === "disabled" && st === "active") return false;
      if (!q) return true;
      return `${s.name || ""} ${s.domain || ""} ${s.id}`.toLowerCase().includes(q);
    });
  }, [sites, query, statusFilter]);

  const addButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
      {t("sites_add_website", "הוסף אתר")}
    </Button>
  );

  const emptyState =
    sites.length === 0 ? (
      <EmptyState
        icon={<LanguageOutlinedIcon />}
        title={t("sites_empty_title", "עוד לא הוספתם אתרים")}
        description={t("sites_no_sites", "אין אתרים עדיין. הוסף אתר ראשון להתחיל.")}
        action={addButton}
      />
    ) : (
      <EmptyState
        icon={<SearchIcon />}
        title={t("sites_no_results", "לא נמצאו אתרים")}
        description={t("sites_no_results_desc", "נסו לשנות את החיפוש או את הסינון.")}
        action={
          <Button
            variant="outlined"
            onClick={() => {
              setQuery("");
              setStatusFilter("all");
            }}
          >
            {t("clear_filters", "ניקוי סינון")}
          </Button>
        }
      />
    );

  const siteIdentity = (s) => (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
      <IconTile size={40} tone={(s.status || "active") === "active" ? "primary" : "neutral"}>
        <LanguageOutlinedIcon />
      </IconTile>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component={Link}
          to={`/sites/${s.id}`}
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            color: "text.primary",
            textDecoration: "none",
            display: "block",
            "&:hover": { color: "primary.main", textDecoration: "underline" },
            "&:focus-visible": { outline: (th) => `2px solid ${th.palette.primary.main}`, outlineOffset: 2, borderRadius: 0.5 },
          }}
          noWrap
        >
          {s.name}
        </Typography>
        <Typography variant="caption" component="div" noWrap>
          {s.domain} · ID {s.id}
        </Typography>
      </Box>
    </Stack>
  );

  // Mobile Card View
  const MobileSiteCard = ({ site }) => (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>{siteIdentity(site)}</Box>
        <IconButton onClick={(e) => handleMenuOpen(e, site)} size="small" aria-label={`${t("sites_table_action", "פעולות")}: ${site.name}`}>
          <MoreVertIcon />
        </IconButton>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
        <StatusPill status={site.status} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SiteKey value={site.site_key} full copied={copiedId === site.id} onCopy={() => copyToClipboard(site.site_key, site.id)} />
        </Box>
      </Stack>
      <Button component={Link} to={`/sites/${site.id}`} variant="outlined" fullWidth startIcon={<TuneRoundedIcon />} sx={{ mt: 2 }}>
        {t("sites_menu_edit", "הגדרות")}
      </Button>
    </Card>
  );

  return (
    <Box>
      <PageHeader title={t("sites", "אתרים")} subtitle={t("sites_subtitle", "ניהול האתרים והקונפיגורציות שלך")} actions={addButton} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <InstallUpgradeNotice sites={sites} onRecheck={() => load().catch(() => {})} />

      {/* Toolbar: search + status segmented filter */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 2 }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("sites_search_placeholder", "חיפוש לפי שם או דומיין")}
          size="small"
          inputProps={{ "aria-label": t("sites_search_placeholder", "חיפוש לפי שם או דומיין") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: (th) => th.aw.faint }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: { sm: 360 }, "& .MuiOutlinedInput-root": { height: 44 } }}
        />
        <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
        <Tabs
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v)}
          aria-label={t("sites_filter_label", "סינון לפי סטטוס")}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab value="all" label={`${t("all", "הכל")} · ${counts.all}`} />
          <Tab value="active" label={`${t("active", "פעילים")} · ${counts.active}`} />
          <Tab value="disabled" label={`${t("disabled_plural", "מושבתים")} · ${counts.disabled}`} />
        </Tabs>
      </Stack>

      {/* Sites Display - Mobile Cards or Desktop Table */}
      {!loaded ? (
        <Card sx={{ py: 8, display: "grid", placeItems: "center" }}>
          <CircularProgress size={28} aria-label="טוען" />
        </Card>
      ) : isMobile ? (
        visibleSites.length === 0 ? (
          <Card>{emptyState}</Card>
        ) : (
          <Stack spacing={1.5}>
            {visibleSites.map((site) => (
              <MobileSiteCard key={site.id} site={site} />
            ))}
          </Stack>
        )
      ) : (
        <Card>
          <TableContainer>
            <Table sx={{ "& td, & th": { px: 2.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("sites_table_name", "שם האתר")}</TableCell>
                  <TableCell>{t("sites_table_status", "סטטוס")}</TableCell>
                  <TableCell>Site Key</TableCell>
                  <TableCell align="right">
                    <Box component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
                      {t("sites_table_action", "פעולות")}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleSites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ borderBottom: 0 }}>
                      {emptyState}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleSites.map((s) => (
                    <TableRow key={s.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={{ maxWidth: 360 }}>{siteIdentity(s)}</TableCell>
                      <TableCell>
                        <StatusPill status={s.status} />
                      </TableCell>
                      <TableCell>
                        <SiteKey value={s.site_key} copied={copiedId === s.id} onCopy={() => copyToClipboard(s.site_key, s.id)} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap", width: 1 }}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Button component={Link} to={`/sites/${s.id}`} size="small" variant="outlined" startIcon={<TuneRoundedIcon />}>
                            {t("sites_menu_edit", "הגדרות")}
                          </Button>
                          <Tooltip title={t("sites_table_action", "פעולות")}>
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, s)}
                              size="small"
                              aria-label={`${t("sites_table_action", "פעולות")}: ${s.name}`}
                              aria-haspopup="menu"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 230 } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem component={Link} to={`/sites/${selectedSite?.id}/onboarding`} onClick={handleMenuClose}>
          <ListItemIcon>
            <RocketLaunchOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_quickstart", "התחלה מהירה")}
        </MenuItem>
        <MenuItem onClick={() => handleWpConnect(selectedSite)}>
          <ListItemIcon>
            <PowerOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_wp_connect", "חיבור אוטומטי לוורדפרס")}
        </MenuItem>
        <MenuItem component={Link} to={`/sites/${selectedSite?.id}`} onClick={handleMenuClose}>
          <ListItemIcon>
            <TuneRoundedIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_edit", "הגדרות")}
        </MenuItem>
        <MenuItem component={Link} to={`/sites/${selectedSite?.id}/install`} onClick={handleMenuClose}>
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_install", "קוד התקנה")}
        </MenuItem>
        <Divider />
        <MenuItem component={Link} to={`/sites/${selectedSite?.id}/statement`} onClick={handleMenuClose}>
          <ListItemIcon>
            <DescriptionOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_statement", "הצהרת נגישות")}
        </MenuItem>
        <MenuItem component={Link} to={`/sites/${selectedSite?.id}/privacy-policy`} onClick={handleMenuClose}>
          <ListItemIcon>
            <PolicyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          מדיניות פרטיות
        </MenuItem>
        <MenuItem component={Link} to={`/sites/${selectedSite?.id}/compliance`} onClick={handleMenuClose}>
          <ListItemIcon>
            <AssessmentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_reports", "דוחות תאימות")}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const id = selectedSite?.id;
            handleMenuClose();
            del(id);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t("sites_menu_delete", "מחיקת אתר")}
        </MenuItem>
      </Menu>

      {/* Add Site Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { mx: 2 } }}>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconTile size={40}>
              <LanguageOutlinedIcon />
            </IconTile>
            <Box>
              <Typography variant="h6" component="h2">
                {t("sites_dialog_title", "הוסף אתר חדש")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("sites_dialog_subtitle", "הגדר את האינטגרציה של האתר שלך")}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Stack spacing={2.5}>
            <TextField
              label={t("siteName", "שם האתר")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder={t("sites_name_placeholder", "האתר שלי")}
              helperText={t("sites_name_help", "שם ידידותי לאתר שלך")}
            />
            <TextField
              label={t("domain", "דומיין")}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              fullWidth
              placeholder="example.com, www.example.com"
              helperText={t("sites_domain_help", "רשימה מופרדת בפסיקים של דומיינים")}
              multiline
              rows={2}
            />

            <Typography variant="overline" component="h3" sx={{ pt: 0.5 }}>
              הגדרות ראשוניות
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: "8px !important" }}>
              <TextField select label="תפריט נגישות" value={enableWidget ? "1" : "0"} onChange={(e) => setEnableWidget(e.target.value === "1")}>
                <MenuItem value="1">פעיל</MenuItem>
                <MenuItem value="0">כבוי</MenuItem>
              </TextField>
              <TextField select label="באנר פרטיות" value={enablePrivacy ? "1" : "0"} onChange={(e) => setEnablePrivacy(e.target.value === "1")}>
                <MenuItem value="1">פעיל</MenuItem>
                <MenuItem value="0">כבוי</MenuItem>
              </TextField>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "text.secondary" }}>
            {t("cancel", "ביטול")}
          </Button>
          <Button variant="contained" onClick={create} disabled={!name || !domain}>
            {t("sites_add_website", "הוסף אתר")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WordPress Plugin Verification Dialog */}
      <Dialog
        open={wpVerifyDialog.open}
        onClose={() => !wpVerifyDialog.loading && setWpVerifyDialog({ ...wpVerifyDialog, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle component="div" sx={{ textAlign: "center", pt: 3 }}>
          <Stack alignItems="center" spacing={1.5}>
            {wpVerifyDialog.loading ? (
              <CircularProgress size={40} aria-label="בודק" />
            ) : (
              <IconTile tone="warning" size={48}>
                <WarningAmberRoundedIcon />
              </IconTile>
            )}
            <Typography variant="h6" component="h2">
              {wpVerifyDialog.loading ? "בודק התקנת תוסף..." : "התוסף לא זוהה באתר"}
            </Typography>
          </Stack>
        </DialogTitle>
        {!wpVerifyDialog.loading && (
          <DialogContent sx={{ pb: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
                כדי לבצע חיבור אוטומטי, עליך להתקין קודם את תוסף ACCSS בוורדפרס שלך.
              </Typography>
              <Box sx={{ p: 2, bgcolor: (th) => th.aw.soft, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                  שלבי התקנה:
                </Typography>
                <Box component="ol" sx={{ m: 0, paddingInlineStart: 2.5, "& li": { fontSize: "0.85rem", mb: 0.5 } }}>
                  <li>הורד את קובץ ה-ZIP של התוסף</li>
                  <li>העלה אותו בלוח הבקרה (תוספים ← חדש)</li>
                  <li>לחץ על "הפעל"</li>
                </Box>
              </Box>
              <Button variant="outlined" fullWidth href="/accss-accessibility.zip" download startIcon={<DownloadRoundedIcon />}>
                הורד תוסף (ZIP)
              </Button>
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ justifyContent: "center", pb: 3, px: 3, gap: 1 }}>
          {!wpVerifyDialog.loading && (
            <>
              <Button onClick={() => setWpVerifyDialog({ ...wpVerifyDialog, open: false })} color="inherit">
                ביטול
              </Button>
              <Button onClick={proceedWpConnect} variant="contained">
                המשך לחיבור בכל זאת
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
